import {
    ReloadOutlined,
    LoadingOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import {
    Button,
    message,
    Form,
    Cascader,
    Space
} from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { loadData, loadDataByDistrict, loadPictureBatch, generateId } from '../Actions/Main-Actions';
import { LOCAL_LOCATION, LOGIN_VIEW } from '../Consts/Consts';
import TableView from './TableView';
import { isValidAdditional } from '../Utils/Utils';
import { DistrictsData } from '../Consts/DataConstants';

import { downloadExcelFromJson, downloadDistrictWiseExcelZip, downloadWelfareSchemeExcel, downloadDistrictWiseWelfareSchemeExcelZip } from '../Utils/ExcelUtils';

const PICTURE_JSON_BATCH_SIZE = 200;

const CardView = () => {

    const [sheetData, setSheetData] = useState();
    const [loading, setLoading] = useState(false);
    const [downloadingPictures, setDownloadingPictures] = useState(false);
    const [pictureDownloadProgress, setPictureDownloadProgress] = useState('');

    const [form] = Form.useForm();
    const [districtCode, setDistrictCode] = useState("");
    const options = DistrictsData;

    const handleDistrictSubSectionClick = (e, _label, option) => {
        e.stopPropagation();
        setDistrictCode(option.code);
    };
    const handleDistrictRender = (labels, selectedOptions) =>
        labels.map((label, i) => {
            const option = selectedOptions[i];
            if (i === labels.length - 1) {
                return (
                    <span key={i} onChange={setDistrictCode(option.code)}>
                        {label} (<a onClick={(e) => handleDistrictSubSectionClick(e, label, option)}>{option.code}</a>)
                    </span>
                );
            }
            return <span key={i}>{label} - </span>;
        });


    const onFinish = async () => {
        setLoading(true)
        let data = {}
        try {
            data = await loadDataByDistrict("DB", districtCode);
            setSheetData(data);
        } catch (error) {
            console.error(error);
            window.localStorage.removeItem(LOCAL_LOCATION);
            navigate(LOGIN_VIEW)
        }
        setLoading(false)
    }
    const navigate = useNavigate();
    const loadTableData = async () => {
        setLoading(true)
        let data = {}
        try {
            data = await loadData("DB");
            setSheetData(data);
        } catch (error) {
            console.error(error);
            window.localStorage.removeItem(LOCAL_LOCATION);
            navigate(LOGIN_VIEW)
        }
        setLoading(false)
    }

    const reloadDataAfterDelete = async () => {
        setLoading(true);
        let data = {};
        try {
            // If a district was selected, reload by district; otherwise reload all
            if (districtCode) {
                data = await loadDataByDistrict("DB", districtCode);
            } else {
                data = await loadData("DB");
            }
            setSheetData(data);
            message.info("Data reloaded successfully");
        } catch (error) {
            console.error(error);
            window.localStorage.removeItem(LOCAL_LOCATION);
            navigate(LOGIN_VIEW);
        }
        setLoading(false);
    }
    const exportData = (data, name) => {
        const file = new Blob([JSON.stringify(data)], { type: 'application/json;charset=utf-8' });
        saveAs(file, `${name}.json`);
    }
    const downloadData = async () => {
        const data = await loadData("DB");
        exportData(data, "RecordData")
    }
    const downloadPicData = async () => {
        const data = await loadData("PIC");
        exportData(data, "PicData")
    }

    const downloadPicsInBatches = async () => {
        if (downloadingPictures) return;
        setDownloadingPictures(true);

        let cursor = null;
        let batchNumber = 1;
        let downloaded = 0;

        try {
            while (true) {
                setPictureDownloadProgress(`Loading picture set ${batchNumber}...`);
                const batch = await loadPictureBatch(cursor, PICTURE_JSON_BATCH_SIZE);
                if (!batch.pictures.length) break;

                const filename = `PicData_${String(batchNumber).padStart(3, '0')}`;
                setPictureDownloadProgress(`Downloading ${filename}.json (${batch.pictures.length} records)...`);
                exportData(batch.pictures, filename);
                downloaded += batch.pictures.length;
                cursor = batch.cursor;
                batchNumber += 1;

                if (batch.pictures.length < PICTURE_JSON_BATCH_SIZE) break;
            }

            if (batchNumber === 1) {
                message.info('No pictures found to download.');
            } else {
                message.success(`Started ${batchNumber - 1} JSON downloads with ${downloaded} picture records.`);
            }
        } catch (error) {
            console.error('Picture download failed:', error);
            message.error(`Picture download stopped after ${batchNumber - 1} JSON files. Please try again.`);
        } finally {
            setPictureDownloadProgress('');
            setDownloadingPictures(false);
        }
    }

    // Download all records as Excel
    const downloadAllExcel = async () => {
        setLoading(true);
        try {
            const data = await loadData("DB");
            if (data && Array.isArray(data)) {
                downloadExcelFromJson(data, 'AllRecords');
            } else {
                message.error('No data found to export.');
            }
        } catch (e) {
            message.error('Failed to download Excel.');
        }
        setLoading(false);
    }

    // Download district-wise Excel zipped
    const downloadDistrictWiseExcel = async () => {
        setLoading(true);
        try {
            const data = await loadData("DB");
            if (data && Array.isArray(data)) {
                await downloadDistrictWiseExcelZip(data, 'DistrictWiseRecords', 'dc');
            } else {
                message.error('No data found to export.');
            }
        } catch (e) {
            message.error('Failed to download district-wise Excel.');
        }
        setLoading(false);
    }

    // Download all records as Excel
    const downloadAllWelfareExcel = async () => {
        setLoading(true);
        try {
            const data = await loadData("DB");
            if (data && Array.isArray(data)) {
                downloadWelfareSchemeExcel(data, 'AllRecordsWelfareScheme');
            } else {
                message.error('No data found to export.');
            }
        } catch (e) {
            message.error('Failed to download Excel.');
        }
        setLoading(false);
    }

    // Download district-wise Excel zipped
    const downloadDistrictWiseWelfareExcel = async () => {
        setLoading(true);
        try {
            const data = await loadData("DB");
            if (data && Array.isArray(data)) {
                await downloadDistrictWiseWelfareSchemeExcelZip(data, 'DistrictWiseRecordsWelfareScheme', 'dc');
            } else {
                message.error('No data found to export.');
            }
        } catch (e) {
            message.error('Failed to download district-wise Excel.');
        }
        setLoading(false);
    }
    const generateIDs = async () => {
        setLoading(true)
        const data = await generateId();
        if (data && data['error'].length === 0) {
            message.info("Generated ID's for the cards !!")
            loadTableData()
            setLoading(false)
        }
        else if (data) {
            message.error("Error in generating the ID's")
            setLoading(false)
        }

    }
    const displayAdditionButtons = () => {
        if (isValidAdditional()) {
            return <>
                <Button type="primary" shape="round" icon={<ReloadOutlined />} onClick={loadTableData}>Load All</Button>
                <Button type="primary" shape="round" icon={<ReloadOutlined />} onClick={downloadData}>Download Records</Button>
                <Button type="primary" shape="round" icon={<DownloadOutlined />} onClick={downloadPicData}>Download Pic Data (JSON)</Button>
                <Button type="primary" shape="round" icon={<DownloadOutlined />} onClick={downloadPicsInBatches} loading={downloadingPictures} disabled={downloadingPictures || loading}>Download Pics (JSON, 200 each)</Button>
                <Button type="primary" shape="round" icon={<ReloadOutlined />} onClick={generateIDs} loading={loading} disabled={loading}>Generate ID's</Button>
                <Button type="primary" shape="round" icon={<ReloadOutlined />} onClick={downloadAllExcel} loading={loading} disabled={loading} >Download All Excel</Button>
                <Button type="primary" shape="round" icon={<ReloadOutlined />} onClick={downloadDistrictWiseExcel} loading={loading} disabled={loading} >Download District wise Excel (Zip)</Button>
                <Button type="primary" shape="round" icon={<ReloadOutlined />} onClick={downloadAllWelfareExcel} loading={loading} disabled={loading} >Download All Excel Welfare Scheme</Button>
                <Button type="primary" shape="round" icon={<ReloadOutlined />} onClick={downloadDistrictWiseWelfareExcel} loading={loading} disabled={loading} >Download District wise Excel Welfare Scheme(Zip)</Button>
            </>
        }

    }

    return (
        <>
            <div>
                <Form
                    form={form}
                    labelCol={{
                        span: 2,
                    }}
                    wrapperCol={{
                        span: 14,
                    }}
                    layout="horizontal"
                    onFinish={onFinish}
                // onReset={onReset}
                >

                    <Form.Item label="District" name='district' rules={[
                        {
                            required: true,
                            message: 'Invalid data - Please select District'
                        },
                    ]}>
                        <Cascader options={options}
                            displayRender={handleDistrictRender}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" shape="round" htmlType="submit" loading={loading} icon={<ReloadOutlined />} >
                                Load Data
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>


            </div>
            <Space>



                {displayAdditionButtons()}
            </Space>
            {loading && <div><LoadingOutlined style={{ fontSize: '16px', color: '#08c' }} /></div>}
            {pictureDownloadProgress && <div role="status">{pictureDownloadProgress}</div>}
            {sheetData && <TableView data={sheetData} onDeleteSuccess={reloadDataAfterDelete} />}
        </>
    )
}
export default CardView;
